import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Typography, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';

interface CancelVisitConfirmModalProps {
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const CANCELLATION_REASONS = [
  { value: 'schedule_change', label: 'Schedule change' },
  { value: 'patient_request', label: 'Patient request' },
  { value: 'medical_reason', label: 'Medical reason' },
  { value: 'insurance_issue', label: 'Insurance issue' },
  { value: 'no_show', label: 'No show' },
  { value: 'other', label: 'Other' },
];

/**
 * Cancel visit confirmation modal - Full screen
 * Shows confirmation with reason dropdown before canceling visit
 */
export function PatientCancelVisitConfirmModal({ onConfirm, onCancel }: CancelVisitConfirmModalProps) {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    if (reason) {
      onConfirm(reason);
    }
  };

  return (
    <FullScreenOverlay>
      <Content>
        <Title>Are you sure?</Title>
        <Message variant="body2">
          Patient&apos;s visit will be completed and marked as canceled. This action cannot be undone.
        </Message>

        <FormControl fullWidth variant="outlined">
          <InputLabel>Cancellation Reason</InputLabel>
          <StyledSelect value={reason} onChange={e => setReason(e.target.value as string)} label="Cancellation Reason">
            {CANCELLATION_REASONS.map(r => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>

        <ButtonGroup>
          <ConfirmButton onClick={handleConfirm} variant="contained" disabled={!reason}>
            Yes
          </ConfirmButton>
          <CancelButton onClick={onCancel} variant="outlined">
            No
          </CancelButton>
        </ButtonGroup>
      </Content>
    </FullScreenOverlay>
  );
}

const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.backgroundColor.string()};
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 600px;
  width: 100%;
`;

const Title = styled(Typography)`
  font-size: 2rem;
  font-weight: 500;
  text-align: center;
  color: white;
`;

const Message = styled(Typography)`
  font-size: 1rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
`;

const StyledSelect = styled(Select)`
  background-color: rgba(255, 255, 255, 0.1);
  color: white;

  .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.3);
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.5);
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #00bcd4;
  }

  .MuiSelect-icon {
    color: white;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  width: 100%;
`;

const ConfirmButton = styled(Button)`
  flex: 1;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  background-color: #00bcd4 !important;
  color: white !important;

  &:hover {
    background-color: #00acc1 !important;
  }

  &:disabled {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: rgba(255, 255, 255, 0.3) !important;
  }
`;

const CancelButton = styled(Button)`
  flex: 1;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
  border-color: rgba(255, 255, 255, 0.3) !important;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2) !important;
  }
`;
